import { html } from '@shared/utils/rendering/html';
import { useState, useEffect } from 'preact/hooks';
import Radio from '@shared/components/forms/form-components/radios';
import { LoaderWithTitle } from '@shared/components/loading/loader-with-title';
import { PrinciplesChart } from './principles-chart';
import { ComponentsChart } from './components-chart';
import { SarbSelfAssessmentService } from '@core/utils/api/sarb-self-assessment.service';
import './index.scss';

const VIEWS = {
    principles: 'principles',
    components: 'components',
};

// Component -> the principles it belongs to, inverted from the server's principle/component
// mapping so the components chart's principle filter can never disagree with the principle
// ratings beside it. This is reference data (the PrincipleComponents formula), not a rating.
const buildComponentPrincipleMap = (principleComponentMap) =>
    principleComponentMap.reduce((acc, { principleNumber, componentIds }) => {
        (componentIds ?? []).forEach((componentId) => {
            (acc[componentId] ??= []).push(Number(principleNumber));
        });
        return acc;
    }, {});

export const SarbCharts = ({ assessmentId, isAssessmentLoading = false, activeFilters = {} }) => {
    const [activeView, setActiveView] = useState(VIEWS.principles);

    const validAssessmentId =
        typeof assessmentId === 'number' && assessmentId > 0 ? assessmentId : null;

    // Method A ratings are computed and rounded to 1dp server-side (SarbRatingsCalculator /
    // SyncSarbFromEvidenceByComponent) and read straight from the assessment's rating endpoints -
    // the client no longer re-derives them from raw subcomponent evidence, so the charts, the
    // overall chip and the grid all show the same numbers. On failure the service returns [], so
    // the charts render empty rather than showing a stale or partial roll-up.
    const [principleRatings, setPrincipleRatings] = useState([]);
    const [componentRatings, setComponentRatings] = useState([]);
    const [isRatingsLoading, setIsRatingsLoading] = useState(false);

    useEffect(() => {
        if (!validAssessmentId) {
            // Also clear the loading flag here: if the id goes invalid while a fetch is in flight,
            // the in-flight .finally() is skipped (cancelled === true), so this branch is the only
            // place left to reset it.
            setPrincipleRatings([]);
            setComponentRatings([]);
            setIsRatingsLoading(false);
            return undefined;
        }

        let cancelled = false;
        setIsRatingsLoading(true);

        Promise.all([
            SarbSelfAssessmentService.getPrincipleRatings(validAssessmentId),
            SarbSelfAssessmentService.getComponentRatings(validAssessmentId),
        ])
            .then(([principles, components]) => {
                if (cancelled) return;
                setPrincipleRatings(principles ?? []);
                setComponentRatings(components ?? []);
            })
            .catch(() => {
                // The service already swallows fetch errors and returns []; this is a belt-and-braces
                // guard so an unexpected rejection can't leave the loader spinning forever.
                if (cancelled) return;
                setPrincipleRatings([]);
                setComponentRatings([]);
            })
            .finally(() => {
                if (!cancelled) setIsRatingsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [validAssessmentId]);

    // Principle -> component grouping is reference data owned by the server, fetched once and used
    // only to drive the components chart's principle filter (not any rating).
    const [componentPrincipleMap, setComponentPrincipleMap] = useState({});

    useEffect(() => {
        let cancelled = false;
        SarbSelfAssessmentService.getPrincipleComponentMap().then((map) => {
            if (!cancelled) setComponentPrincipleMap(buildComponentPrincipleMap(map ?? []));
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleViewChange = (view) => setActiveView(view);

    const viewField = { name: 'sarb-chart-view', value: activeView, setValue: handleViewChange };

    if (isAssessmentLoading) {
        return html`
            <div class="card sarb-graph-card mb-3">
                <div
                    class="card-body d-flex align-items-center justify-content-center"
                    style="min-height:300px"
                >
                    <${LoaderWithTitle} busyLoading=${true} title="Loading..." />
                </div>
            </div>
        `;
    }

    if (!validAssessmentId) {
        return null;
    }

    return html`
        <div class="card sarb-graph-card mb-3">
            <div class="card-header bg-white">
                <span class="fw-semibold d-block mb-2">Graph</span>
                <div class="d-flex gap-3">
                    <${Radio}
                        css="sarb-graph-radio"
                        field=${viewField}
                        value=${VIEWS.principles}
                        label=${{ text: 'Principles' }}
                    />
                    <${Radio}
                        css="sarb-graph-radio"
                        field=${viewField}
                        value=${VIEWS.components}
                        label=${{ text: 'Components' }}
                    />
                </div>
            </div>
            <div class="card-body" style="position:relative">
                <div class="${activeView !== VIEWS.principles ? 'sarb-chart-offscreen' : ''}">
                    <${PrinciplesChart}
                        principleRatings=${principleRatings}
                        isLoading=${isRatingsLoading}
                        selectedPrinciple=${activeFilters.principle ?? null}
                    />
                </div>
                <div class="${activeView !== VIEWS.components ? 'sarb-chart-offscreen' : ''}">
                    <${ComponentsChart}
                        componentRatings=${componentRatings}
                        componentPrincipleMap=${componentPrincipleMap}
                        isLoading=${isRatingsLoading}
                        selectedPrinciple=${activeFilters.principle ?? null}
                    />
                </div>
            </div>
        </div>
    `;
};
